import { useState, useEffect, useRef, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useTVRemote } from './hooks/useTVRemote'
import { doc, updateDoc, increment, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase/config'
import { toast } from './hooks/useToast'
import { useAuth } from './hooks/useAuth'
import { useVisitorTracking } from './hooks/useVisitorTracking'
import fcmService from './services/fcmService'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import GamePage from './pages/GamePage'

// Styles
import './styles/index.css'
import './styles/androidtv.css'
import './styles/tv-mode-fixes.css'
import './styles/tv-mode-responsive.css'
import './styles/tv-viewport-fix.css'
import './styles/johnny-ui.css' // toujours en dernier
import { loadPrefs, PREFS_EVENT } from './utils/appPrefs'

// Context
import { UserProvider } from './hooks/useUser'

// Layout
import MoreSheet          from './components/Layout/MoreSheet'
import DockBar            from './components/Layout/DockBar'
import AnimatedBackground from './components/Layout/AnimatedBackground'
import Toast              from './components/Common/Toast'
import Loader             from './components/Common/Loader'

// Pages
import RadioPage         from './pages/RadioPage'
import ToolsPage         from './pages/ToolsPage'
import PlatformsPage     from './pages/PlatformsPage'
import DedicacesPage     from './pages/DedicacesPage'
import BlogPage          from './pages/BlogPage'
import PodcastsPage      from './pages/PodcastsPage'
import PressePage        from './pages/PressePage'
import ContactPage       from './pages/ContactPage'
import ProfilePage       from './pages/ProfilePage'
import PlanningPage      from './pages/PlanningPage'
import EventsPage        from './pages/EventsPage'
import GalleryPage       from './pages/GalleryPage'
import AbonnementPage    from './pages/AbonnementPage'
import BoutiquePage      from './pages/BoutiquePage'
import AdminPage         from './pages/AdminPage'
import SettingsPage      from './pages/SettingsPage'
import AboutPage         from './pages/AboutPage'
import RadioPageTV       from './pages/RadioPageTV'
import ProfilPageTV      from './pages/ProfilPageTV'
import ClassementPageTV  from './pages/ClassementPageTV'
import ParametresPageTV  from './pages/ParametresPageTV'
import AboutPageTV       from './pages/AboutPageTV'
import NotreUniversPage    from './pages/NotreUniversPage'
import RADIO_CONFIG from './config/radioConfig'

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════
const STREAM_URL = RADIO_CONFIG.STREAM_URL
const FCM_VAPID_KEY = 'BJaNYtB0eZ-N8_iBXNpb1-7ZqBtjSBFBFHqniTbYFvxrKiioPQjIejqNhxR4srmiU2xQLKFcjO9u7amyQ5VycPE'

// ═══════════════════════════════════════════════════════════
// DÉTECTION TV
// ═══════════════════════════════════════════════════════════
const detectTVDevice = () => {
  const ua = navigator.userAgent.toLowerCase()
  const w  = window.innerWidth
  const h  = window.innerHeight
  const isAndroidTV =
    ua.includes('android') &&
    (ua.includes('tv') || ua.includes('aftm') || ua.includes('aft') || ua.includes('smart-tv'))
  const isLargeScreen = w >= 1280 && h >= 720
  const hasNoTouch    = !('ontouchstart' in window)
  return isAndroidTV || (isLargeScreen && hasNoTouch)
}

// Navigation télécommande (doit vivre dans le Router pour connaître la page)
function TVRemoteManager({ enabled }) {
  const { pathname } = useLocation()
  useTVRemote(enabled, pathname)
  return null
}

// ═══════════════════════════════════════════════════════════
// APP
// ═══════════════════════════════════════════════════════════
function App() {

  // ── Auth ─────────────────────────────────────────────────
  const { currentUser, loadingAuth, handleLogout: authLogout } = useAuth()
  
    // ── Visiteurs (web uniquement) ─────────────────────────────
  useVisitorTracking()

  // ── Logout ────────────────────────────────────────────────
  const handleLogout = async () => {
    await authLogout()
  }


  // ── UI ───────────────────────────────────────────────────
  const [isMobileDevice, setIsMobileDevice] = useState(() => window.innerWidth < 768)
  const [isTVDevice,     setIsTVDevice]     = useState(() => window.innerWidth >= 768 && detectTVDevice())
  const [moreOpen, setMoreOpen] = useState(false)
  const [prefs, setPrefs] = useState(() => loadPrefs())

  // ── Audio ────────────────────────────────────────────────
  const [isPlaying,    setIsPlaying]    = useState(false)
  const [isMuted,      setIsMuted]      = useState(false)
  const [volume,       setVolume]       = useState(() => {
    const saved = localStorage.getItem('audioVolume')
    return saved ? parseInt(saved) : loadPrefs().defaultVolume
  })
  const [currentTrack, setCurrentTrack] = useState({
    title:  'Que Je T\'aime',
    artist: 'Johnny Hallyday',
    cover:  null
  })

  // ── Refs ─────────────────────────────────────────────────
  const audioRef          = useRef(null)
  const wakeLockRef       = useRef(null)
  const isMobileDeviceRef = useRef(isMobileDevice)
  const isTVDeviceRef     = useRef(isTVDevice)
  const isPlayingRef      = useRef(false)
  const listenIntervalRef = useRef(null)

  // ── Sync refs ────────────────────────────────────────────
  useEffect(() => {
    isTVDeviceRef.current = isTVDevice
    // Classe utilisée par johnny-ui.css pour alléger l'affichage TV
    document.documentElement.classList.toggle('is-tv', isTVDevice)
  }, [isTVDevice])
  useEffect(() => { isMobileDeviceRef.current = isMobileDevice }, [isMobileDevice])
  useEffect(() => { isPlayingRef.current      = isPlaying      }, [isPlaying])

  // ═══════════════════════════════════════════════════════
  // TEMPS D'ÉCOUTE — ICI et pas dans RadioPage/RadioPageTV.
  // L'audio (audioRef) vit ici, dans App.jsx, qui ne se démonte
  // jamais tant que l'appli est ouverte. Le suivi doit vivre au
  // même endroit : sinon il s'arrête dès qu'on quitte l'onglet
  // Radio (le composant de page est détruit), alors que la radio,
  // elle, continue de jouer en fond — d'où le temps d'écoute
  // qui restait bloqué à 0.
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (!currentUser || !isPlaying) {
      if (listenIntervalRef.current) {
        clearInterval(listenIntervalRef.current)
        listenIntervalRef.current = null
      }
      return undefined
    }

    const updateListenTime = async () => {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          totalListenTime: increment(30),
          lastListenedAt: serverTimestamp()
        })
      } catch (error) {
        console.error('Erreur temps écoute:', error)
      }
    }

    listenIntervalRef.current = setInterval(updateListenTime, 30000)

    return () => {
      if (listenIntervalRef.current) {
        clearInterval(listenIntervalRef.current)
        listenIntervalRef.current = null
      }
    }
  }, [currentUser, isPlaying])

  // ═══════════════════════════════════════════════════════
  // PRÉCHARGEMENT DES IMAGES CRITIQUES
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    const preload = (src) => {
      const image = new Image()
      image.decoding = 'async'
      image.fetchPriority = 'high'
      image.src = src
    }

    preload('/images/logo_ic.png')
    preload('/images/logo.png')
  }, [])

  // ═══════════════════════════════════════════════════════
  // DÉTECTION MOBILE
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    const checkDevice = () => {
      const m = window.innerWidth < 768
      setIsMobileDevice(m)
      isMobileDeviceRef.current = m
      // Réévalué à chaque redimensionnement : avant, une fenêtre ouverte
      // en grand puis rétrécie restait bloquée en mode TV (sans barre).
      const tv = !m && detectTVDevice()
      setIsTVDevice(tv)
      isTVDeviceRef.current = tv
    }
    checkDevice()
    window.addEventListener('resize', checkDevice)
    return () => window.removeEventListener('resize', checkDevice)
  }, [])

  // ═══════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════
  // FCM — Réception messages en premier plan (web uniquement)
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return

    const handleMessage = (payload) => {
      try {
        const title = payload?.notification?.title || 'Notification'
        const body  = payload?.notification?.body  || ''
        toast.success(`🔔 ${title}: ${body}`)
        if (Notification.permission === 'granted') {
          new Notification(title, {
            body,
            icon:  '/images/logo.png',
            badge: '/images/logo.png'
          })
        }
      } catch (err) {
        console.error('❌ Erreur message FCM:', err)
      }
    }

    if (fcmService?.on) fcmService.on('onMessage', handleMessage)
    return () => { if (fcmService?.off) fcmService.off('onMessage', handleMessage) }
  }, [])

  // ═══════════════════════════════════════════════════════
  // RÉCUPÉRATION TRACK
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const res  = await fetch('https://api.radioking.io/widget/radio/radio-johnny-hallyday/track/current')
        const data = await res.json()
        if (data?.title) {
          // On ne met à jour que si la chanson a changé : sinon toute
          // l'app se redessinait toutes les 10 s (saccades sur TV).
          setCurrentTrack(prev => (
            prev.title === data.title &&
            prev.artist === (data.artist || 'Johnny Hallyday') &&
            prev.cover === data.cover
          ) ? prev : {
            title:  data.title,
            artist: data.artist || 'Johnny Hallyday',
            cover:  data.cover
          })
        }
      } catch (err) {
        console.error('Erreur fetch track:', err)
      }
    }
    fetchTrack()
    const interval = setInterval(fetchTrack, 10000)
    return () => clearInterval(interval)
  }, [])

  // ═══════════════════════════════════════════════════════
  // VOLUME
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100
    }
  }, [volume, isMuted])

  // ═══════════════════════════════════════════════════════
  // SURVEILLANCE AUDIO (TV / MOBILE / PC)
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (!isPlaying || !audioRef.current) return
    const audio    = audioRef.current
    const isTV     = isTVDeviceRef.current
    const isMobile = isMobileDeviceRef.current

    // ── MODE TV ──────────────────────────────────────────
    if (isTV) {
      let frozenCounter = 0
      let lastTime      = audio.currentTime
      let errorCount    = 0

      const interval = setInterval(() => {
        if (!isPlayingRef.current) return
        const currentTime = audio.currentTime
        const timeDiff    = Math.abs(currentTime - lastTime)
        if (timeDiff < 0.1 && !audio.paused && currentTime !== 0) {
          frozenCounter++
          if (frozenCounter >= 5) {
            console.warn('🚨 TV : relance douce après 50s')
            errorCount++
            if (errorCount < 3) {
              audio.play().catch(() => { audio.load(); audio.play().catch(console.error) })
            } else {
              audio.load(); audio.play().catch(console.error)
              errorCount = 0
            }
            frozenCounter = 0
          }
        } else {
          frozenCounter = 0
          if (timeDiff > 1) errorCount = 0
        }
        lastTime = currentTime
      }, 10000)

      const onError = () => {
        if (!isPlayingRef.current) return
        setTimeout(() => {
          if (isPlayingRef.current && audioRef.current) {
            audioRef.current.play().catch(() => {
              audioRef.current.load(); audioRef.current.play().catch(console.error)
            })
          }
        }, 10000)
      }
      audio.addEventListener('error', onError)
      return () => { clearInterval(interval); audio.removeEventListener('error', onError) }
    }

    // ── MODE MOBILE ───────────────────────────────────────
    if (isMobile) {
      let frozenCounter   = 0
      let lastTime        = audio.currentTime
      let silenceDetected = false

      const interval = setInterval(() => {
        if (!isPlayingRef.current) return
        const currentTime = audio.currentTime
        const timeDiff    = Math.abs(currentTime - lastTime)
        if (timeDiff < 0.1 && !audio.paused && currentTime !== 0) {
          frozenCounter++
          if (frozenCounter >= 2) {
            audio.play().catch(() => { audio.load(); audio.play().catch(console.error) })
            frozenCounter   = 0
            silenceDetected = true
          }
        } else {
          if (silenceDetected && timeDiff > 0.5) silenceDetected = false
          frozenCounter = 0
        }
        lastTime = currentTime
        if (audio.readyState < 2) audio.play().catch(console.error)
      }, 5000)

      const onStalled = () => { audio.play().catch(() => { audio.load(); audio.play().catch(console.error) }) }
      const onWaiting = () => {
        setTimeout(() => {
          if (audio.readyState < 3 && isPlayingRef.current) audio.play().catch(console.error)
        }, 2000)
      }
      const onSuspend = () => { if (isPlayingRef.current) setTimeout(() => audio.play().catch(console.error), 1000) }
      const onError   = () => {
        if (!isPlayingRef.current) return
        setTimeout(() => {
          if (isPlayingRef.current && audioRef.current) {
            audioRef.current.load(); audioRef.current.play().catch(console.error)
          }
        }, 2000)
      }
      audio.addEventListener('stalled', onStalled)
      audio.addEventListener('waiting', onWaiting)
      audio.addEventListener('suspend', onSuspend)
      audio.addEventListener('error',   onError)
      return () => {
        clearInterval(interval)
        audio.removeEventListener('stalled', onStalled)
        audio.removeEventListener('waiting', onWaiting)
        audio.removeEventListener('suspend', onSuspend)
        audio.removeEventListener('error',   onError)
      }
    }

    // ── MODE PC ───────────────────────────────────────────
    let lastTime = audio.currentTime

    const recoverAudio = () => {
      if (!isPlayingRef.current) return
      if (audio.networkState === 3 || audio.readyState < 2) {
        audio.load(); audio.play().catch(console.error)
      }
    }
    audio.addEventListener('stalled', recoverAudio)
    audio.addEventListener('waiting', recoverAudio)

    const interval = setInterval(() => {
      if (!audio.paused) {
        if (audio.currentTime === lastTime && audio.currentTime !== 0) {
          console.warn('🚨 PC : Flux figé, relance...')
          audio.play().catch(() => { audio.load(); audio.play() })
        }
        lastTime = audio.currentTime
      }
    }, 5000)

    return () => {
      clearInterval(interval)
      audio.removeEventListener('stalled', recoverAudio)
      audio.removeEventListener('waiting', recoverAudio)
    }
  }, [isPlaying])

  // ═══════════════════════════════════════════════════════
  // WAKE LOCK
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (!isPlaying) {
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(console.error); wakeLockRef.current = null }
      return
    }
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen')
        }
      } catch (err) { /* Wake Lock non supporté */ }
    }
    requestWakeLock()

    const handleVisibilityChange = () => {
      if (Capacitor.isNativePlatform()) return
      if (document.visibilityState === 'visible' && isPlaying) {
        requestWakeLock()
        if (audioRef.current?.paused) audioRef.current.play().catch(console.error)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (wakeLockRef.current) wakeLockRef.current.release().catch(console.error)
    }
  }, [isPlaying])

  // ═══════════════════════════════════════════════════════
  // MEDIA SESSION
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (!currentTrack || !('mediaSession' in navigator)) return
    navigator.mediaSession.metadata = new MediaMetadata({
      title:   currentTrack.title  || 'Radio 100% Johnny',
      artist:  currentTrack.artist || 'Johnny Hallyday',
      album:   '100% Johnny - En Direct',
      artwork: ['96x96','128x128','192x192','256x256','384x384','512x512'].map(s => ({
        src:   currentTrack.cover || '/images/logo.png',
        sizes: s,
        type:  currentTrack.cover ? 'image/jpeg' : 'image/png'
      }))
    })
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused'
    navigator.mediaSession.setActionHandler('play',  async () => { if (!isPlaying) await toggleGlobalPlay() })
    navigator.mediaSession.setActionHandler('pause', ()      => { if (isPlaying)  toggleGlobalPlay() })
    navigator.mediaSession.setActionHandler('stop',  ()      => {
      if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false) }
    })
    try {
      navigator.mediaSession.setActionHandler('seekbackward',  null)
      navigator.mediaSession.setActionHandler('seekforward',   null)
      navigator.mediaSession.setActionHandler('previoustrack', null)
      navigator.mediaSession.setActionHandler('nexttrack',     null)
    } catch (e) {}
    return () => { navigator.mediaSession.playbackState = 'none' }
  }, [isPlaying, currentTrack])

  // ═══════════════════════════════════════════════════════
  // CAPACITOR APP STATE
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    const listener = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive && isPlaying) {
        if (audioRef.current?.paused) audioRef.current.play().catch(console.error)
      }
    })
    return () => { listener.remove() }
  }, [isPlaying])

  // ═══════════════════════════════════════════════════════
  // EVENTS AUDIO GÉNÉRIQUES
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (!audioRef.current) return
    const audio = audioRef.current
    const onWaiting = () => console.log('⏳ Buffering...')
    const onPlaying = () => console.log('▶️ Playing')
    const onError   = (e) => {
      if (isPlayingRef.current) {
        console.error('❌ Audio error:', e)
        setTimeout(() => {
          if (audioRef.current && isPlayingRef.current) {
            audioRef.current.load()
            audioRef.current.play().catch(() => setIsPlaying(false))
          }
        }, 3000)
      }
    }
    audio.addEventListener('waiting', onWaiting)
    audio.addEventListener('playing', onPlaying)
    audio.addEventListener('error',   onError)
    return () => {
      audio.removeEventListener('waiting', onWaiting)
      audio.removeEventListener('playing', onPlaying)
      audio.removeEventListener('error',   onError)
    }
  }, [])

  // ═══════════════════════════════════════════════════════
  // PLAY / PAUSE
  // ═══════════════════════════════════════════════════════
  const toggleGlobalPlay = async () => {
    if (!audioRef.current) { toast.error('❌ Audio non initialisé'); return }
    const audio = audioRef.current

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      console.log('⏸️ Pause')
    } else {
      try {
        audio.src = STREAM_URL
        audio.load()
        audio.volume = isMuted ? 0 : volume / 100
        await audio.play()
        setIsPlaying(true)
        console.log('▶️ Lecture OK')
        toast.success('🎵 Radio démarrée')
      } catch (error) {
        console.error('❌ Erreur play:', error.name, error.message)
        if (error.name === 'NotAllowedError') {
          toast.error('❌ Cliquez sur Play pour démarrer la radio')
        } else if (error.name === 'NotSupportedError') {
          toast.error('❌ Format audio non supporté')
        } else {
          toast.error('❌ Erreur de lecture audio')
        }
        setIsPlaying(false)
      }
    }
  }

  // ═══════════════════════════════════════════════════════
  // CONTRÔLES VOLUME
  // ═══════════════════════════════════════════════════════
  const handleVolumeUp = () => {
    setVolume(v => {
      const n = Math.min(100, v + 10)
      localStorage.setItem('audioVolume', n.toString())
      if (n > 0 && isMuted) {
        setIsMuted(false)
        localStorage.setItem('audioMuted', 'false')
        if (audioRef.current) audioRef.current.volume = n / 100
      }
      toast.info(`🔊 Volume: ${n}%`)
      return n
    })
  }

  const handleVolumeDown = () => {
    setVolume(v => {
      const n = Math.max(0, v - 10)
      localStorage.setItem('audioVolume', n.toString())
      if (n === 0) {
        setIsMuted(true)
        localStorage.setItem('audioMuted', 'true')
        if (audioRef.current) audioRef.current.volume = 0
      }
      toast.info(n === 0 ? '🔇 Son coupé' : `🔉 Volume: ${n}%`)
      return n
    })
  }

  const handleToggleMute = () => {
    setIsMuted(m => {
      const n = !m
      localStorage.setItem('audioMuted', String(n))
      if (audioRef.current) audioRef.current.volume = n ? 0 : volume / 100
      toast.info(n ? '🔇 Muté' : '🔊 Son activé')
      return n
    })
  }

  // ═══════════════════════════════════════════════════════
  // PRÉFÉRENCES (page Paramètres) — appliquées en direct
  // ═══════════════════════════════════════════════════════
  const closeMore = useCallback(() => setMoreOpen(false), [])

  useEffect(() => {
    const onPrefs = (e) => setPrefs(e.detail || loadPrefs())
    window.addEventListener(PREFS_EVENT, onPrefs)
    return () => window.removeEventListener(PREFS_EVENT, onPrefs)
  }, [])

  // ═══════════════════════════════════════════════════════
  // PUB D'OUVERTURE (native) ↔ RADIO
  // MainActivity.java envoie :
  //  - "johnnyAdShowing"  : la pub s'affiche → on coupe la radio
  //  - "johnnyAdFinished" : pub fermée (ou aucune pub) → on reprend,
  //    et on lance la lecture automatique si elle est activée
  // ═══════════════════════════════════════════════════════
  const resumeAfterAdRef = useRef(false)
  const autoplayDoneRef  = useRef(false)

  const startAutoplay = useCallback(async () => {
    if (autoplayDoneRef.current) return
    // L'élément <audio> n'existe qu'après le chargement de la connexion
    if (!audioRef.current) {
      setTimeout(() => startAutoplayRef.current?.(), 400)
      return
    }
    autoplayDoneRef.current = true
    if (!loadPrefs().autoplay || isPlayingRef.current) return
    try {
      const audio = audioRef.current
      audio.src = STREAM_URL
      audio.load()
      await audio.play()
      setIsPlaying(true)
    } catch {
      // Le navigateur a refusé la lecture automatique : on n'insiste pas
    }
  }, [])
  const startAutoplayRef = useRef(startAutoplay)
  startAutoplayRef.current = startAutoplay

  useEffect(() => {
    const onShowing = () => {
      const audio = audioRef.current
      if (audio && isPlayingRef.current && !audio.paused) {
        resumeAfterAdRef.current = true
        audio.pause()
      }
    }

    const onFinished = () => {
      if (resumeAfterAdRef.current) {
        resumeAfterAdRef.current = false
        audioRef.current?.play().catch(() => {})
      }
      startAutoplay()
    }

    window.addEventListener('johnnyAdShowing', onShowing)
    window.addEventListener('johnnyAdFinished', onFinished)

    // Pas de pub d'ouverture sur web et TV, ou pub déjà terminée
    // avant que l'app web soit prête : lecture auto directement.
    let fallback = null
    if (!Capacitor.isNativePlatform() || isTVDeviceRef.current || window.__johnnyAdDone) {
      startAutoplay()
    } else {
      // Filet de sécurité si le natif ne répond pas
      fallback = setTimeout(startAutoplay, 12000)
    }

    return () => {
      window.removeEventListener('johnnyAdShowing', onShowing)
      window.removeEventListener('johnnyAdFinished', onFinished)
      if (fallback) clearTimeout(fallback)
    }
  }, [startAutoplay])

  // ═══════════════════════════════════════════════════════
  // OBJET AUDIO GLOBAL
  // ═══════════════════════════════════════════════════════
  const globalAudio = {
    isPlaying,
    setIsPlaying,
    volume,
    setVolume,
    isMuted,
    setIsMuted,
    audioRef,
    togglePlay:     toggleGlobalPlay,
    volumeUp:       handleVolumeUp,
    volumeDown:     handleVolumeDown,
    toggleMute:     handleToggleMute,
    currentTrack,
    setCurrentTrack
  }

  // ═══════════════════════════════════════════════════════
  // RENDER — Loading
  // ═══════════════════════════════════════════════════════
   // ══ RENDER — Loading ══
   if (loadingAuth) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '4px solid rgba(255,215,0,0.2)',
            borderTopColor: '#ffd700',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#ffd700', fontSize: '1.1rem', fontWeight: 700 }}>
            100% JOHNNY
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }//
  
  // ═══════════════════════════════════════════════════════
  // RENDER — App
  // ═══════════════════════════════════════════════════════
  return (
    <UserProvider value={currentUser}>
      <Router>
        <div className="app">
          <TVRemoteManager enabled={isTVDevice} />
          <Loader />
          <AnimatedBackground disabled={isTVDevice || !prefs.backgroundFx} />

          <audio ref={audioRef} preload="none" playsInline />

          {isMobileDevice && (
            <MoreSheet
              open={moreOpen}
              onClose={closeMore}
              currentUser={currentUser}
            />
          )}

          <div className="app-content">
            <Routes>

              <Route
                path="/"
                element={
                  isMobileDevice
                    ? <RadioPage   currentUser={currentUser} globalAudio={globalAudio} />
                    : <RadioPageTV currentUser={currentUser} globalAudio={globalAudio} />
                }
              />

              <Route path="/profile"    element={<ProfilePage       currentUser={currentUser} onLogout={handleLogout} />} />
              <Route path="/profil"     element={<ProfilPageTV      currentUser={currentUser} onLogout={handleLogout} />} />
              <Route path="/classement" element={<ClassementPageTV  currentUser={currentUser} />} />
              <Route path="/parametres" element={<ParametresPageTV  currentUser={currentUser} globalAudio={globalAudio} />} />
              <Route path="/a-propos"   element={<AboutPageTV />} />

              <Route
                path="/platforms"
                element={<PlatformsPage />}
              />

              <Route
                path="/notre-univers"
                element={<NotreUniversPage />}
              />

              <Route path="/tools"      element={<ToolsPage globalAudio={globalAudio} />} />
			  <Route path="/game"       element={<GamePage />} />
              <Route path="/dedicaces"  element={<DedicacesPage  currentUser={currentUser} />} />
              <Route path="/blog"       element={<BlogPage       currentUser={currentUser} />} />
              <Route path="/podcasts"   element={<PodcastsPage   currentUser={currentUser} />} />
              <Route path="/presse"     element={<PressePage     currentUser={currentUser} />} />
              <Route path="/contact"    element={<ContactPage />} />
              <Route path="/planning"   element={<PlanningPage   currentUser={currentUser} />} />
              <Route path="/events"     element={<EventsPage     currentUser={currentUser} />} />
              <Route path="/abonnement" element={<AbonnementPage currentUser={currentUser} />} />
              <Route path="/boutique"   element={<BoutiquePage currentUser={currentUser} />} />
              <Route path="/gallery"    element={<GalleryPage />} />
              <Route path="/admin"      element={<AdminPage      currentUser={currentUser} />} />
              <Route path="/settings"   element={<SettingsPage   currentUser={currentUser} globalAudio={globalAudio} />} />
              <Route path="/about"      element={<AboutPage />} />

              <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
          </div>

          {isMobileDevice && (
            <DockBar
              onMoreClick={() => setMoreOpen(o => !o)}
              moreOpen={moreOpen}
              isPlaying={isPlaying}
            />
          )}

          <Toast />
        </div>
      </Router>
    </UserProvider>
  )
}

export default App